import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';
import { useSipsAutomationPreferences, useUpdateSipsAutomationPreferences } from '../../../hooks/useSipsAutomationPreferences';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { useAdvisorConfig } from '../../../hooks/useAdvisorConfig';
import { getFirmAutomationPreferences } from '../../../services/firmService';
import { SipsAutomationPreferences, enforceRothInvariant } from '../../../types/automationPreferences';
import AutomationPreferencesForm, { TIME_HORIZON_AGES } from './AutomationPreferencesForm';

export { TIME_HORIZON_AGES };

interface SipsAutomationPreferencesEditorProps {
  customerId: string;
  onSave?: () => void;
}

/**
 * Per-advisor editor for SIPS automation preferences (loads/saves through the
 * per-customer SIPS proxy). The form body is the shared, controlled
 * AutomationPreferencesForm — also used by the firm/enterprise defaults
 * editors. Advisors under a firm additionally get "Reset to enterprise
 * defaults", which loads the firm's default preferences into the form as
 * pending edits (nothing is written to SIPS until Save).
 */
const SipsAutomationPreferencesEditor: React.FC<SipsAutomationPreferencesEditorProps> = ({
  customerId,
  onSave
}) => {
  const { preferences, loading, error, refresh } = useSipsAutomationPreferences(customerId);
  const { update, updating, error: updateError } = useUpdateSipsAutomationPreferences();
  const { token } = useAuth0Token();
  const { config: advisorConfig } = useAdvisorConfig(customerId || null, true);
  const firmId = advisorConfig?.firmId || null;

  const [editedPrefs, setEditedPrefs] = useState<Partial<SipsAutomationPreferences>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [enterpriseLoading, setEnterpriseLoading] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState<string | null>(null);
  const [enterpriseLoaded, setEnterpriseLoaded] = useState(false);

  // Initialize edited prefs when preferences load
  useEffect(() => {
    if (preferences) {
      // A stored set that skips SC06.01 but not the what-ifs reads (and, once
      // saved, persists) as all-off — the Roth invariant.
      setEditedPrefs(enforceRothInvariant({ ...preferences }));
      setHasChanges(false);
    }
  }, [preferences]);

  const handleFormChange = (next: Partial<SipsAutomationPreferences>) => {
    setEditedPrefs(next);
    setHasChanges(true);
    setSaveSuccess(false);
    setEnterpriseLoaded(false);
  };

  const handleSave = async () => {
    const result = await update(customerId, editedPrefs);
    if (result) {
      setSaveSuccess(true);
      setHasChanges(false);
      setEnterpriseLoaded(false);
      await refresh();
      onSave?.();
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const handleReset = () => {
    if (preferences) {
      setEditedPrefs({ ...preferences });
      setHasChanges(false);
      setEnterpriseLoaded(false);
    }
  };

  // Load the firm's default preferences into the form as pending edits — the
  // advisor reviews and saves; nothing is pushed to SIPS until Save.
  const handleLoadEnterpriseDefaults = async () => {
    if (!token || !firmId) return;
    setEnterpriseLoading(true);
    setEnterpriseError(null);
    try {
      const { preferences: firmPrefs } = await getFirmAutomationPreferences(token, firmId);
      setEditedPrefs((prev) => enforceRothInvariant({ ...prev, ...firmPrefs }));
      setHasChanges(true);
      setSaveSuccess(false);
      setEnterpriseLoaded(true);
    } catch (err: any) {
      setEnterpriseError(err.response?.data?.message || 'Failed to load enterprise defaults');
    } finally {
      setEnterpriseLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load automation preferences: {error}
        <Button size="small" onClick={refresh} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!preferences) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No automation preferences found for this customer.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with Save/Reset buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">
          SIPS Automation Preferences
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          {firmId && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={enterpriseLoading ? <CircularProgress size={20} /> : <RestartAltIcon />}
              onClick={handleLoadEnterpriseDefaults}
              disabled={updating || enterpriseLoading}
            >
              Reset to Enterprise Defaults
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={!hasChanges || updating}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || updating}
          >
            {updating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {(updateError || saveSuccess || enterpriseError || enterpriseLoaded) && (
        <Box mb={2}>
          {updateError && (
            <Alert severity="error" onClose={() => {}}>
              {updateError}
            </Alert>
          )}
          {enterpriseError && (
            <Alert severity="error" onClose={() => setEnterpriseError(null)}>
              {enterpriseError}
            </Alert>
          )}
          {enterpriseLoaded && (
            <Alert severity="info">
              Enterprise defaults loaded — review below and click Save Changes to apply them.
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success">
              Preferences saved successfully!
            </Alert>
          )}
        </Box>
      )}

      {/* Read-only flag warning */}
      {!preferences.allowPlanScoutAutomationFlag && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          PlanScout automation is not enabled for this customer in SIPS.
          Please enable it in SIPS to use automation features.
        </Alert>
      )}

      <AutomationPreferencesForm value={editedPrefs} onChange={handleFormChange} />
    </Box>
  );
};

export default SipsAutomationPreferencesEditor;
